import React, { Fragment } from "react";
import PropTypes from "prop-types";
import CSSTransitionGroup from "react-transition-group/CSSTransitionGroup";
import {
  regexNameCreateUserApi,
  regexSearchBoxExpedient,
  regexSearchBoxLookup,
} from "utils/regexExpressions";
import { Col } from "reactstrap";
import { useTranslation } from "react-i18next";
import cx from "classnames";

const SearchBoxLookupField = (props) => {
  const { t } = useTranslation();
  const {
    searchDraft,
    setSearchDraft,
    setSearchData,
    paginationRecords,
    setPaginationRecords,
    errorCharacters,
    setErrorCharacters,
    isRecordListExpedient,
    isApiUserSearch,
  } = props;
  let regexSearchValue = null;

  /**
   * Determines the appropriate regex search value based on the given condition.
   * @param {boolean} isRecordListExpedient - A flag indicating whether the record list is expedient.
   * @param {RegExp} regexSearchBoxExpedient - The regex pattern to use if `isRecordListExpedient` is true.
   * @param {RegExp} regexSearchBoxLookup - The regex pattern to use if `isRecordListExpedient` is false.
   * @returns {RegExp} - The selected regex search pattern.
   */
  if (isApiUserSearch === true) {
    regexSearchValue = regexNameCreateUserApi;
  } else if (isRecordListExpedient === true) {
    regexSearchValue = regexSearchBoxExpedient;
  } else {
    regexSearchValue = regexSearchBoxLookup;
  }

  /**
   * Handles changes to the search input value and updates the search data state, validates the input against a regex pattern.
   * @param {Object} eventChange - The event object representing the change in the input field.
   */
  const handleOnChangeSearch = (eventChange) => {
    const searchValueTiped = eventChange.target.value;
    const searchValueEncoded = encodeURIComponent(searchValueTiped);

    if (searchValueTiped === "" || regexSearchValue.test(searchValueTiped)) {
      setErrorCharacters("");
    } else {
      setErrorCharacters(t("createRoles.InputInvalid"));
    }
    setSearchDraft(searchValueEncoded);
  };

  /**
   * Handles search validation before triggering a specified action.
   * This function checks if pagination records are defined, prevents the default action of the event,
   * and then validates the search data using a regular expression (`regex`). If the search data
   * passes the validation, the specified action function is called. *
   * @param {object} event - The event object associated with the triggering action.
   * This is typically an event object from a form submission or a button click.
   * @param {function} action - The action function to be executed if the search validation passes.
   * This function will only be called if the provided search data passes the validation.
   * @returns {void}
   */
  const handleSearchValidation = (event, action) => {
    if (paginationRecords !== undefined) {
      event.preventDefault();

      if ((searchDraft === "" || regexSearchValue.test(searchDraft)) && errorCharacters === "") {
        action();
      }
    }
  };

  /**
   * Handles keypress events for search functionality, specifically the "Enter" key.
   * Confirms the draft search value and triggers search validation,
  * resetting pagination to the first page.
   * @param {KeyboardEvent} eventSearch - The keypress event object for the search input field.
   * @param {object} paginationRecords - The pagination records object.
   * @param {function} setPaginationRecords - The state setter function for updating pagination records.
   */
  const handleOnKeyPressForSearch = (eventSearch) => {
    if (eventSearch.key === "Enter") {
      setSearchData(searchDraft);
      handleSearchValidation(eventSearch, () =>
        setPaginationRecords({ ...paginationRecords, page: 1 })
      );
    }
  };

  /**
   * Handles the click event of the open button, resetting the pagination page to 1 if paginationRecords is defined.
   * @param {Event} eventSearch - The click event object triggered when the open button is clicked.
   * @param {object} paginationRecords - The pagination records object.
   * @param {function} setPaginationRecords - The state setter function for updating pagination records.
   * @returns {void}
   */
  const handleOnClickOpenButton = (eventSearch) => {
    handleSearchValidation(eventSearch, () => {
      setSearchData(searchDraft);
      setPaginationRecords({ ...paginationRecords, page: 1 });
    });
  };

  return (
    <Fragment>
      <CSSTransitionGroup
        component="div"
        transitionName="TabsAnimation"
        transitionAppear={true}
        transitionAppearTimeout={0}
        transitionEnter={false}
        transitionLeave={false}
      >
        <Col lg="auto" md="12" sm="12" className="align-self-end">
          <div
            className={cx("search-wrapperExplorer ml-auto search-box", {
              active: true,
            })}
          >
            <div className="input-holder btn-block ml-auto btn-icon btn-mb-responsive">
              <input
                type="text"
                className="search-input"
                placeholder={t("searchbox.placeholder")}
                onKeyDown={handleOnKeyPressForSearch}
                onChange={handleOnChangeSearch}
                value={decodeURIComponent(searchDraft)}
              />
              <button className="search-icon" onClick={handleOnClickOpenButton}>
                <span />
              </button>
            </div>
            {errorCharacters && <span className="text-danger text small">{errorCharacters}</span>}
          </div>
        </Col>
      </CSSTransitionGroup>
    </Fragment>
  );
};

SearchBoxLookupField.propTypes = {
  searchDraft: PropTypes.string.isRequired,
  setSearchDraft: PropTypes.func.isRequired,
  setSearchData: PropTypes.func.isRequired,
  searchData: PropTypes.string.isRequired,
  paginationRecords: PropTypes.object.isRequired,
  setPaginationRecords: PropTypes.func.isRequired,
  errorCharacters: PropTypes.string.isRequired,
  setErrorCharacters: PropTypes.func.isRequired,
  isRecordListExpedient: PropTypes.bool.isRequired,
  isApiUserSearch: PropTypes.bool,
};

export default SearchBoxLookupField;
